'use client'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { omit } from 'lodash'
import { useTranslations } from 'next-intl'
import {
  createContext,
  type ReactNode,
  useContext,
  useRef,
  useState,
} from 'react'

type ContextType = {
  confirm: (content: Partial<ContentType>) => Promise<boolean>
  isOpen: boolean
}

const ConfirmContext = createContext({} as ContextType)

type ContentType = {
  title?: string
  description?: string
  okText?: string
  cancelText?: string
  showCancel?: boolean
  onOk?: (open: boolean) => void
  okProps?: { tooltip?: string; disabled?: boolean; [key: string]: unknown }
  okClassName?: string
}

const defaultContent: ContentType = {
  okText: 'OK',
  cancelText: 'Cancel',
  showCancel: true,
}

export default function ConfirmProvider({ children }: { children: ReactNode }) {
  const [openConfirm, setOpenConfirm] = useState(false)
  const t = useTranslations('ui')
  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const [content, setContent] = useState<ContentType>(defaultContent)

  async function confirm({
    title,
    description,
    okText,
    cancelText,
    showCancel,
    onOk,
    okProps,
    okClassName,
  }: Partial<ContentType>): Promise<boolean> {
    setContent({
      title,
      description,
      cancelText: cancelText ?? defaultContent.cancelText,
      okText: okText ?? defaultContent.okText,
      showCancel: showCancel ?? defaultContent.showCancel,
      onOk,
      okProps,
      okClassName,
    })
    setOpenConfirm(true)

    return await new Promise((resolve) => {
      resolveRef.current = resolve
    })
  }

  return (
    <ConfirmContext.Provider value={{ confirm, isOpen: openConfirm }}>
      <AlertDialog
        open={openConfirm}
        onOpenChange={(open) => {
          if (!open) {
            if (resolveRef.current) resolveRef.current(false)
          }
          setOpenConfirm(open)
        }}
      >
        <AlertDialogContent
          id="alert-dialog-content"
          className="bg-modal-gradient rounded-lg border-modal-border-color !z-[9999]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{content.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-wrap break-all">
              {content.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {content.showCancel && (
              <AlertDialogCancel
                onClick={() => resolveRef.current?.(false)}
                className="bg-white/10 hover:bg-white/20 text-white hover:no-underline"
              >
                {t('cancel')}
              </AlertDialogCancel>
            )}

            {content.okProps?.tooltip ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <AlertDialogAction
                      className={cn(
                        'bg-white hover:bg-white/70 text-gray-700 rounded-lg border-1 disabled:opacity-50 disabled:cursor-not-allowed',
                        content.okClassName,
                      )}
                      onClick={() => {
                        if (resolveRef.current) resolveRef.current(true)

                        setOpenConfirm(false)
                        content.onOk?.(true)
                      }}
                      {...omit(content.okProps, ['tooltip'])}
                    >
                      {content.okText}
                    </AlertDialogAction>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{content.okProps?.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <AlertDialogAction
                className={cn(
                  'bg-white hover:bg-white/70 text-gray-700 rounded-lg border-1 disabled:opacity-50 disabled:cursor-not-allowed',
                  content.okClassName,
                )}
                onClick={() => {
                  if (resolveRef.current) resolveRef.current(true)

                  setOpenConfirm(false)
                  content.onOk?.(true)
                }}
                {...content.okProps}
              >
                {content.okText}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {children}
    </ConfirmContext.Provider>
  )
}

export const useConfirm = () => {
  return useContext(ConfirmContext)
}
